"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Hammer, Layers, ShieldCheck, Sparkles, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const pillars = [{
  icon: Hammer,
  title: "Structural reconfiguration",
  description: "Remove compartmentalized partition walls and insert flush steel or LVL beams to create airy, connected gathering spaces.",
  metric: "Open-Concept Living"
}, {
  icon: Wrench,
  title: "Modern MEP infrastructure",
  description: "Overhaul aging plumbing stacks, upgrade 200-amp electrical panels, and balance zoned heat pumps for lasting peace of mind.",
  metric: "Efficiency & Safety"
}, {
  icon: Sparkles,
  title: "Unified architectural millwork",
  description: "Run continuous white oak flooring, bespoke casing profiles, and custom built-in cabinetry throughout your main levels.",
  metric: "Tailored Detailing"
}, {
  icon: Layers,
  title: "Phased project scheduling",
  description: "Execute remodeling in clean, predictable stages with daily dust containment barriers and direct on-site project coordination.",
  metric: "Clear Milestones"
}];
const faqs = [{
  question: "Can our family remain living in the home during construction?",
  answer: "For comprehensive whole-home transformations involving primary kitchen and main bath demolition, we generally advise temporary off-site living for comfort. However, for phased multi-level renovations, we build negative-pressure zip-wall dust containment, protect pathways daily, and maintain dedicated functional living zones."
}, {
  question: "How long does permitting take with the City of Brookhaven and DeKalb County?",
  answer: "Plan review and building permit issuance in Brookhaven typically spans 3 to 6 weeks. Our team manages all structural drawings, zoning clearances, and code submittals directly with local municipal inspectors so you avoid administrative delays."
}, {
  question: "What should homeowners anticipate for whole-home renovation investments?",
  answer: "Whole-home renovation investment varies depending on total square footage, structural engineering scope—such as bearing wall alterations—and the finish level selected for cabinetry, surfaces, and fixtures. We deliver transparent line-item pricing before physical construction begins."
}];
export default function Remodeling() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="whole-home-remodeling" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Top Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUpVariant} className="max-w-3xl mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary-foreground font-semibold px-3 py-1 rounded-full text-xs tracking-wider uppercase">
              Residential Renovation
            </Badge>
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
              Brookhaven & North Atlanta
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.18] mb-6">
            Whole-home renovations, shaped around how you live
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            From removing load-bearing walls to modernizing mechanical systems, we renew every room with steady guidance, skilled trade partners, and transparent timelines.
          </p>
        </motion.div>

        {/* Asymmetrical Layout: Narrative Column (5 cols) & Feature Pillars (7 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column (5 cols): Narrative + Sticky Architectural Context */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeUpVariant} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm flex flex-col gap-6">
              <h3 className="font-serif text-2xl font-semibold text-foreground">
                One unified vision, handled under a single roof
              </h3>

              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Tackling a whole-home renovation room by room can lead to disjointed finishes and recurring disruptions. Our design-build model brings your floor plan, millwork specifications, and mechanical planning into one coordinated workflow.
              </p>

              <div className="space-y-4 pt-2 border-t border-border">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">
                      General contracting & project management
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Full-scope coordination handling municipal permit submittals, trade scheduling, and required inspections.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">
                      Dedicated on-site supervision
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Direct daily updates from your project coordinator to keep work on schedule.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6 py-3 font-medium transition-[background-color,box-shadow]">
                  <Link to="/ContactRequestAnEstimatePage">
                    Schedule a Consultation
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Architectural Visual Accent */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeUpVariant} className="relative rounded-2xl overflow-hidden border border-border aspect-[4/3] bg-muted shadow-sm group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation_interior.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation_interior.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation_interior.png-wvc-sizes" alt="Open-concept residential living and dining area in Brookhaven" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-secondary/20 to-transparent flex flex-col justify-end p-6">
                <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                  Featured Brookhaven Project
                </span>
                <p className="text-sm font-medium text-secondary-foreground mt-1">
                  Complete interior renovation with engineered beam integration & wide-plank oak.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right Column (7 cols): Renovation Pillars Grid + Divided FAQ */}
          <div className="lg:col-span-7 flex flex-col gap-12">
            {/* 4 Renovation Pillar Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {pillars.map((pillar, i) => {
              const IconComponent = pillar.icon;
              return <motion.div key={pillar.title} data-index={i} initial="hidden" whileInView="visible" viewport={{
                once: true,
                amount: 0.15
              }} variants={{
                hidden: {
                  opacity: 0,
                  y: shouldReduceMotion ? 0 : 15
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                    delay: shouldReduceMotion ? 0 : i * 0.08,
                    ease: [0.16, 1, 0.3, 1]
                  }
                }
              }} whileHover={shouldReduceMotion ? {} : {
                y: -4,
                transition: {
                  duration: 0.25,
                  ease: "easeOut"
                }
              }} className="rounded-2xl border border-border bg-card/80 backdrop-blur-md p-6 shadow-sm hover:border-primary/50 hover:shadow-md transition-[border-color,box-shadow] flex flex-col justify-between">
                    <div>
                      <div className="size-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <span className="text-xs font-semibold text-primary uppercase tracking-wider block mb-1">
                        {pillar.metric}
                      </span>
                      <h3 className="font-serif text-lg font-semibold text-foreground mb-2">
                        {pillar.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {pillar.description}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>

            {/* Homeowner Planning & FAQ Section */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeUpVariant} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="mb-6">
                <span className="text-xs uppercase tracking-widest text-primary font-semibold block mb-1">
                  Planning Considerations
                </span>
                <h3 className="font-serif text-2xl font-semibold text-foreground">
                  Common whole-home remodeling questions
                </h3>
              </div>

              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <div key={faq.question} data-index={i}>
                    <AccordionItem value={`item-${i}`} className="border-border py-1">
                      <AccordionTrigger className="text-left font-sans font-semibold text-foreground hover:no-underline hover:text-primary py-4 text-base">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </div>)}
              </Accordion>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}